<template>
  <div class="container-fluid py-4">
    <div class="row mb-4">
      <div class="col-12">
        <nav aria-label="breadcrumb">
          <ol class="breadcrumb">
            <li class="breadcrumb-item"><router-link to="/dashboard">Dashboard</router-link></li>
            <li class="breadcrumb-item"><router-link to="/performance/reviews">Performance Reviews</router-link></li>
            <li class="breadcrumb-item active" aria-current="page">360 Feedback</li>
          </ol>
        </nav>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else>
      <!-- Feedback Header -->
      <div class="card mb-4">
        <div class="card-body">
          <div class="row align-items-center">
            <div class="col-md-8">
              <h4 class="mb-2">360 Feedback for {{ feedbackRequest?.employee?.name || feedbackRequest?.review?.employee?.full_name || ((feedbackRequest?.review?.employee?.first_name || '') + ' ' + (feedbackRequest?.review?.employee?.last_name || '')) }}</h4>
              <p class="text-muted mb-0">
                Review Period: {{ feedbackRequest?.review_cycle?.name || feedbackRequest?.review?.review_cycle?.name }}
              </p>
            </div>
            <div class="col-md-4 text-md-end">
              <span :class="getDeadlineBadgeClass()">
                <i class="icon-feather-clock me-1"></i>
                Due: {{ formatDate(feedbackRequest?.review?.review_cycle?.end_date) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Employee Information -->
      <div class="card mb-4">
        <div class="card-header">
          <h5 class="mb-0">Employee Information</h5>
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-md-6">
              <div class="mb-3">
                <label class="text-muted">Name:</label>
                <p class="mb-0"><strong>{{ feedbackRequest?.employee?.name || feedbackRequest?.review?.employee?.full_name || ((feedbackRequest?.review?.employee?.first_name || '') + ' ' + (feedbackRequest?.review?.employee?.last_name || '')) }}</strong></p>
              </div>
            </div>
            <div class="col-md-6">
              <div class="mb-3">
                <label class="text-muted">Your Relationship:</label>
                <p class="mb-0">{{ formatRelationship(feedbackRequest?.relationship) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Feedback Form -->
      <form @submit.prevent="submitFeedback">
        <!-- Competency Ratings -->
        <div class="card mb-4">
          <div class="card-header">
            <h5 class="mb-0">Competency Assessment</h5>
            <small class="text-muted">Rate the employee on the following competencies</small>
          </div>
          <div class="card-body">
            <div v-for="competency in competencies" :key="competency.id" class="mb-4">
              <div class="row align-items-start g-3">
                <div class="col-lg-4 col-md-5">
                  <label class="form-label mb-0">
                    <strong>{{ competency.name }} <span class="text-danger">*</span></strong>
                    <small class="d-block text-muted">{{ competency.description }}</small>
                  </label>
                </div>
                <div class="col-lg-5 col-md-7">
                  <div class="btn-group w-100 flex-wrap gap-2" role="group" aria-label="Competency rating options">
                    <input
                      type="radio"
                      :id="`${competency.id}_na`"
                      :name="`rating_${competency.id}`"
                      :value="0"
                      v-model="formData.ratings[competency.id]"
                      class="btn-check"
                      required
                    >
                    <label :for="`${competency.id}_na`" class="btn btn-outline-secondary">N/A</label>

                    <input
                      type="radio"
                      :id="`${competency.id}_1`"
                      :name="`rating_${competency.id}`"
                      :value="1"
                      v-model="formData.ratings[competency.id]"
                      class="btn-check"
                      required
                    >
                    <label :for="`${competency.id}_1`" class="btn btn-outline-danger">Poor</label>

                    <input
                      type="radio"
                      :id="`${competency.id}_2`"
                      :name="`rating_${competency.id}`"
                      :value="2"
                      v-model="formData.ratings[competency.id]"
                      class="btn-check"
                      required
                    >
                    <label :for="`${competency.id}_2`" class="btn btn-outline-warning">Fair</label>

                    <input
                      type="radio"
                      :id="`${competency.id}_3`"
                      :name="`rating_${competency.id}`"
                      :value="3"
                      v-model="formData.ratings[competency.id]"
                      class="btn-check"
                      required
                    >
                    <label :for="`${competency.id}_3`" class="btn btn-outline-info">Good</label>

                    <input
                      type="radio"
                      :id="`${competency.id}_4`"
                      :name="`rating_${competency.id}`"
                      :value="4"
                      v-model="formData.ratings[competency.id]"
                      class="btn-check"
                      required
                    >
                    <label :for="`${competency.id}_4`" class="btn btn-outline-primary">Very Good</label>

                    <input
                      type="radio"
                      :id="`${competency.id}_5`"
                      :name="`rating_${competency.id}`"
                      :value="5"
                      v-model="formData.ratings[competency.id]"
                      class="btn-check"
                      required
                    >
                    <label :for="`${competency.id}_5`" class="btn btn-outline-success">Excellent</label>
                  </div>
                </div>
                <div class="col-lg-3 col-md-12">
                  <input
                    v-model="formData.comments[competency.id]"
                    type="text"
                    class="form-control mt-1 mt-md-0"
                    :placeholder="`Optional comment...`"
                  >
                </div>
                <hr>
              </div>
            </div>
          </div>
        </div>

        <!-- Overall Assessment -->
        <div class="card mb-4">
          <div class="card-header">
            <h5 class="mb-0">Overall Assessment</h5>
          </div>
          <div class="card-body">
            <!-- Strengths -->
            <div class="mb-3">
              <label class="form-label">
                What are this employee's key strengths?
                <span class="text-danger">*</span>
              </label>
              <textarea
                v-model="formData.strengths"
                class="form-control"
                rows="3"
                placeholder="Describe the employee's strengths and positive contributions..."
                required
              ></textarea>
            </div>

            <!-- Areas for Improvement -->
            <div class="mb-3">
              <label class="form-label">
                What areas could this employee improve?
                <span class="text-danger">*</span>
              </label>
              <textarea
                v-model="formData.improvements"
                class="form-control"
                rows="3"
                placeholder="Describe areas where the employee could develop or improve..."
                required
              ></textarea>
            </div>

            <!-- Additional Comments -->
            <div class="mb-3">
              <label class="form-label">
                Additional Comments
              </label>
              <textarea
                v-model="formData.additional_comments"
                class="form-control"
                rows="3"
                placeholder="Any additional feedback or observations..."
              ></textarea>
            </div>

            <!-- Overall Rating -->
            <div class="mb-3">
              <label class="form-label">
                Overall Performance Rating
                <span class="text-danger">*</span>
              </label>
              <div class="btn-group d-flex flex-wrap gap-2 w-100" role="group" aria-label="Overall performance rating options">
                <input
                  type="radio"
                  id="overall_unsatisfactory"
                  name="overall_rating"
                  value="unsatisfactory"
                  v-model="formData.overall_rating"
                  class="btn-check"
                  required
                >
                <label for="overall_unsatisfactory" class="btn btn-outline-danger">
                  Unsatisfactory
                </label>

                <input
                  type="radio"
                  id="overall_needs_improvement"
                  name="overall_rating"
                  value="needs_improvement"
                  v-model="formData.overall_rating"
                  class="btn-check"
                  required
                >
                <label for="overall_needs_improvement" class="btn btn-outline-warning">
                  Needs Improvement
                </label>

                <input
                  type="radio"
                  id="overall_meets"
                  name="overall_rating"
                  value="meets"
                  v-model="formData.overall_rating"
                  class="btn-check"
                  required
                >
                <label for="overall_meets" class="btn btn-outline-info">
                  Meets Expectations
                </label>

                <input
                  type="radio"
                  id="overall_exceeds"
                  name="overall_rating"
                  value="exceeds"
                  v-model="formData.overall_rating"
                  class="btn-check"
                  required
                >
                <label for="overall_exceeds" class="btn btn-outline-primary">
                  Exceeds Expectations
                </label>

                <input
                  type="radio"
                  id="overall_exceptional"
                  name="overall_rating"
                  value="exceptional"
                  v-model="formData.overall_rating"
                  class="btn-check"
                  required
                >
                <label for="overall_exceptional" class="btn btn-outline-success">
                  Exceptional
                </label>
              </div>
            </div>

            <!-- Recommendation -->
            <div class="mb-3">
              <label class="form-label">
                Would you recommend this employee for promotion?
                <span class="text-danger">*</span>
              </label>
              <div class="btn-group d-flex flex-wrap gap-2 w-100" role="group" aria-label="Promotion recommendation options">
                <input
                  type="radio"
                  id="recommend_yes"
                  name="recommendation"
                  value="yes"
                  v-model="formData.recommend_promotion"
                  class="btn-check"
                  required
                >
                <label for="recommend_yes" class="btn btn-outline-success">
                  <i class="icon-feather-check me-1"></i>Yes
                </label>

                <input
                  type="radio"
                  id="recommend_maybe"
                  name="recommendation"
                  value="maybe"
                  v-model="formData.recommend_promotion"
                  class="btn-check"
                >
                <label for="recommend_maybe" class="btn btn-outline-warning">
                  <i class="icon-feather-help-circle me-1"></i>Maybe
                </label>

                <input
                  type="radio"
                  id="recommend_no"
                  name="recommendation"
                  value="no"
                  v-model="formData.recommend_promotion"
                  class="btn-check"
                >
                <label for="recommend_no" class="btn btn-outline-danger">
                  <i class="icon-feather-x me-1"></i>No
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Form Actions -->
        <div class="card">
          <div class="card-body">
            <small class="text-muted mt-2 mt-md-0">
              <i class="icon-feather-info me-1"></i>
              Your feedback will remain confidential
            </small>
            <div class="d-flex flex-column flex-md-row justify-content-between align-items-stretch gap-3 mt-2">
              <div class="d-grid gap-2 d-md-flex ms-md-auto w-100 w-md-auto">
                <button
                    type="button"
                    @click="saveDraft"
                    class="btn btn-outline-secondary w-100 w-md-auto me-0 me-md-2"
                    :disabled="submitting"
                >
                  <i class="icon-feather-save me-2"></i>Save as Draft
                </button>
                <button
                  type="button"
                  @click="$router.back()"
                  class="btn btn-light w-100 w-md-auto me-0 me-md-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  class="btn btn-primary w-100 w-md-auto"
                  :disabled="submitting"
                >
                  <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
                  <i v-else class="icon-feather-send me-2"></i>
                  Submit Feedback
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { usePerformanceStore } from '@/stores/performance';
import feedbackRequestService from '@/api/feedbackRequestService';
import moment from 'moment';
import notify from '@/utils/notify';

export default {
  name: 'FeedbackForm',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const performanceStore = usePerformanceStore();

    const loading = ref(true);
    const submitting = ref(false);
    const feedbackRequest = ref(null);
    const competencies = ref([]);

    // Popular competency questions to assess
    const POPULAR_COMPETENCIES = [
      { id: 'communication', name: 'Communication', description: 'Clearly conveys information and actively listens to others.' },
      { id: 'teamwork', name: 'Teamwork & Collaboration', description: 'Works well with others and contributes to team goals.' },
      { id: 'problem_solving', name: 'Problem Solving', description: 'Identifies issues and develops effective solutions.' },
      { id: 'accountability', name: 'Accountability', description: 'Takes ownership of tasks and meets commitments.' },
      { id: 'initiative', name: 'Initiative', description: 'Proactively takes on responsibilities and seeks improvements.' },
      { id: 'adaptability', name: 'Adaptability', description: 'Adjusts effectively to changes and new challenges.' },
      { id: 'quality', name: 'Quality of Work', description: 'Produces accurate, thorough, and high-quality outputs.' },
      { id: 'time_management', name: 'Time Management', description: 'Prioritizes tasks and meets deadlines efficiently.' },
      { id: 'customer_focus', name: 'Customer Focus', description: 'Understands and meets internal/external customer needs.' },
      { id: 'leadership', name: 'Leadership', description: 'Guides others, influences outcomes, and sets a positive example.' }
    ];

    const formData = ref({
      ratings: {},
      comments: {},
      strengths: '',
      improvements: '',
      additional_comments: '',
      overall_rating: '',
      recommend_promotion: ''
    });

    const formatDate = (date) => {
      if (!date) return 'N/A';
      return moment(date).format('MMM DD, YYYY');
    };

    const formatRelationship = (relationship) => {
      const relationships = {
        peer: 'Peer/Colleague',
        subordinate: 'Direct Report',
        manager: 'Manager',
        cross_functional: 'Cross-functional Partner',
        client: 'Client/Customer',
        other: 'Other'
      };
      return relationships[relationship] || relationship;
    };

    const getDeadlineBadgeClass = () => {
      const endDate = feedbackRequest.value?.review?.review_cycle?.end_date;
      if (!endDate) return 'badge bg-secondary';

      const daysUntilDeadline = moment(endDate).diff(moment(), 'days');

      if (daysUntilDeadline < 0) return 'badge bg-danger';
      if (daysUntilDeadline <= 3) return 'badge bg-warning';
      return 'badge bg-info';
    };

    const loadFeedbackRequest = async () => {
      try {
        loading.value = true;

        // Load feedback request details by ID
        const id = route.params.id;
        feedbackRequest.value = await performanceStore.fetchFeedbackRequest(id);

        // Build competency assessment based on popular ones
        competencies.value = POPULAR_COMPETENCIES;

        // Load draft if exists
        const draftKey = `feedback_draft_${feedbackRequest.value?.id || id}`;
        const draft = localStorage.getItem(draftKey);
        if (draft) {
          const draftData = JSON.parse(draft);
          formData.value = { ...formData.value, ...draftData };
        }
      } catch (error) {
        console.error('Failed to load feedback request:', error);
        notify.alert.error('Failed to load feedback form');
        router.push('/performance/reviews');
      } finally {
        loading.value = false;
      }
    };

    const saveDraft = () => {
      const draftKey = `feedback_draft_${feedbackRequest.value?.id || route.params.id}`;
      localStorage.setItem(draftKey, JSON.stringify(formData.value));
      notify.alert.success('Draft saved successfully');
    };

    const validateForm = () => {
      if (!formData.value.strengths || !formData.value.improvements) {
        notify.alert.error('Please provide strengths and areas for improvement');
        return false;
      }

      if (!formData.value.overall_rating) {
        notify.alert.error('Please provide an overall rating');
        return false;
      }

      // Ensure all competencies have a selection (including N/A)
      const missingCompetencies = (competencies.value || []).filter(c => formData.value.ratings[c.id] === undefined).length;
      if (missingCompetencies > 0) {
        notify.alert.error('Please rate all competencies (you can select N/A where not applicable)');
        return false;
      }

      // Ensure recommendation is selected
      if (!formData.value.recommend_promotion) {
        notify.alert.error('Please provide a promotion recommendation');
        return false;
      }

      return true;
    };

    const submitFeedback = async () => {
      if (!validateForm()) return;

      submitting.value = true;

      try {
        // Calculate average score from ratings
        const ratings = Object.values(formData.value.ratings).filter(r => r > 0);
        const averageScore = ratings.length > 0
          ? Math.round((ratings.reduce((a, b) => a + b, 0) / ratings.length) * 20)
          : 0;

        // Build feedback payload compatible with feedback-requests update
        const id = feedbackRequest.value?.id || route.params.id;
        const feedback = {
          categories: formData.value.ratings,
          category_comments: formData.value.comments,
          strengths: formData.value.strengths,
          improvements: formData.value.improvements,
          additional_comments: formData.value.additional_comments,
          overall_rating: formData.value.overall_rating,
          recommend_promotion: formData.value.recommend_promotion,
          average_score: averageScore
        };

        await feedbackRequestService.update(id, feedback);

        // Clear draft
        const draftKey = `feedback_draft_${feedbackRequest.value?.id || route.params.id}`;
        localStorage.removeItem(draftKey);

        notify.alert.success('Feedback submitted successfully');
        router.push('/my-feedback');
      } catch (error) {
        console.error('Failed to submit feedback:', error);
        notify.alert.error('Failed to submit feedback');
      } finally {
        submitting.value = false;
      }
    };

    onMounted(() => {
      loadFeedbackRequest();
    });

    return {
      loading,
      submitting,
      feedbackRequest,
      competencies,
      formData,
      formatDate,
      formatRelationship,
      getDeadlineBadgeClass,
      saveDraft,
      submitFeedback
    };
  }
};
</script>

<style scoped>
.btn-check:checked + .btn-outline-danger {
  background-color: #dc3545;
  color: white;
}

.btn-check:checked + .btn-outline-warning {
  background-color: #ffc107;
  color: black;
}

.btn-check:checked + .btn-outline-info {
  background-color: #17a2b8;
  color: white;
}

.btn-check:checked + .btn-outline-primary {
  background-color: #007bff;
  color: white;
}

.btn-check:checked + .btn-outline-success {
  background-color: #28a745;
  color: white;
}

.btn-check:checked + .btn-outline-secondary {
  background-color: #6c757d;
  color: white;
}
</style>
